import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag } from '@/components/accounting/posting-rules/status-badge';
import { formatDate } from '@/lib/utils';
import { entryHref, type TieOutApp, type UnmirroredEntry } from './model';

interface UnmirroredTableProps {
  app: TieOutApp;
  entries: UnmirroredEntry[];
  /** Legacy lists show why an entry is excluded from repair. */
  showReason?: boolean;
}

/** Posted entries with party-linked lines that have no subledger rows. */
export function UnmirroredTable({ app, entries, showReason = false }: UnmirroredTableProps) {
  const { t } = useTranslation('tieOut');
  if (entries.length === 0) return <p className="px-3 py-4 text-sm text-muted-foreground">{t('unmirrored.none')}</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-medium">{t('unmirrored.columns.date')}</th>
            <th className="px-3 py-2 font-medium">{t('unmirrored.columns.entry')}</th>
            <th className="px-3 py-2 font-medium">{t('unmirrored.columns.description')}</th>
            <th className="px-3 py-2 font-medium">{t('unmirrored.columns.reference')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('unmirrored.columns.partyLines')}</th>
            {showReason && <th className="px-3 py-2 font-medium">{t('unmirrored.columns.reason')}</th>}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.entryId} className="border-b border-border last:border-b-0">
              <td className="px-3 py-2 whitespace-nowrap">{formatDate(entry.date)}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                <Link to={entryHref(app, entry.entryId)} className="font-mono text-xs hover:underline">
                  {entry.entryNumber}
                </Link>
              </td>
              <td className="px-3 py-2">{entry.description}</td>
              <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                {entry.referenceType ?? '—'}
                {entry.postingKey && <span className="ml-1 font-mono">{entry.postingKey}</span>}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{entry.partyLines}</td>
              {showReason && (
                <td className="px-3 py-2">
                  <Tag className="text-amber-700 dark:text-amber-300">{t(`unmirrored.reason.${entry.reason}`)}</Tag>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
